import React, { useEffect, useState } from "react";
import { Wrench, Send, FileText, CreditCard, CheckCircle2, Clock, Hammer, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Demande": "bg-brand-soft text-brand-green",
  "Devis propos\u00e9": "bg-blue-100 text-blue-700",
  "Acompte pay\u00e9": "bg-amber-100 text-amber-700",
  "Termin\u00e9": "bg-brand-lime/30 text-brand-greenDeep",
};

export default function Travaux() {
  const { user } = useAuth();
  const isArtisan = user?.role === "artisan";
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [quote, setQuote] = useState({ total_amount: "", deposit_amount: "", note: "" });
  const [aiLoading, setAiLoading] = useState(false);

  const endpoint = isArtisan ? "/works/artisan" : "/works/my";
  const load = () => api.get(endpoint).then((r) => { setList(r.data); if (!activeId && r.data.length) setActiveId(r.data[0].id); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeId) api.get(`/works/${activeId}`).then((r) => setActive(r.data)).catch(() => {}); }, [activeId, list]);

  const sendMsg = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    await api.post(`/works/${activeId}/messages`, { text: t });
    api.get(`/works/${activeId}`).then((r) => setActive(r.data));
  };

  const proposeQuote = async () => {
    if (!quote.total_amount || !quote.deposit_amount) { toast.error("Montant total et acompte requis"); return; }
    await api.put(`/works/${activeId}/quote`, {
      total_amount: Number(quote.total_amount), deposit_amount: Number(quote.deposit_amount), note: quote.note,
    });
    toast.success("Devis envoy\u00e9 au client");
    setQuote({ total_amount: "", deposit_amount: "", note: "" });
    load();
  };

  const generateAiQuote = async () => {
    setAiLoading(true);
    try {
      const res = await api.post(`/works/${activeId}/ai-quote`);
      setQuote({ total_amount: res.data.total_amount || "", deposit_amount: res.data.deposit_amount || "", note: res.data.note || "" });
      toast.success("Devis pr\u00e9-rempli par l'IA \u2014 ajustez si besoin");
    } catch (e) { toast.error("L'IA n'a pas pu g\u00e9n\u00e9rer le devis"); } finally { setAiLoading(false); }
  };

  const pay = async (phase) => {
    try {
      const res = await api.post(`/works/${activeId}/pay`, { phase, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de paiement"); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-160px)]">
      <div className="bg-white rounded-2xl border border-brand-soft overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-brand-soft"><h3 className="font-semibold text-brand-greenDeep">{isArtisan ? "Demandes re\u00e7ues" : "Mes demandes de travaux"}</h3></div>
        <div className="flex-1 overflow-y-auto">
          {list.map((w) => (
            <button key={w.id} onClick={() => setActiveId(w.id)} className={`w-full text-left px-5 py-4 border-b border-brand-soft transition-colors ${activeId === w.id ? "bg-brand-softer" : "hover:bg-brand-softer/50"}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-sm text-brand-greenDeep">{isArtisan ? w.client_name : w.artisan_name}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[w.status] || "bg-brand-soft"}`}>{w.status}</span>
              </div>
              <p className="text-xs text-brand-green/50 truncate mt-1">{w.domain} · {w.description}</p>
            </button>
          ))}
          {list.length === 0 && <p className="text-sm text-brand-green/50 p-5">Aucune demande pour le moment.</p>}
        </div>
      </div>

      <div className="lg:col-span-2 bg-white rounded-2xl border border-brand-soft flex flex-col overflow-hidden">
        {active ? (
          <>
            <div className="px-6 py-4 border-b border-brand-soft flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center"><Wrench className="w-5 h-5 text-brand-greenDeep" /></span>
                <div>
                  <p className="font-semibold text-brand-greenDeep">{isArtisan ? active.client_name : active.artisan_name}</p>
                  <p className="text-xs text-brand-green/50">{active.domain}</p>
                </div>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full font-semibold ${STATUS_STYLE[active.status] || "bg-brand-soft"}`}>{active.status}</span>
            </div>

            {/* Diagnostic IA */}
            {active.diagnostic && (
              <div className="mx-6 mt-4 rounded-2xl border border-brand-soft bg-brand-softer/60 p-4" data-testid="work-diagnostic">
                <div className="flex items-center gap-2 text-brand-greenDeep font-semibold mb-2"><Sparkles className="w-4 h-4 text-brand-limeDark" /> Diagnostic IA</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-lime/30 text-brand-greenDeep">{active.diagnostic.category}</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${active.diagnostic.urgency === "Critique" ? "bg-red-100 text-red-700" : active.diagnostic.urgency === "Élevée" ? "bg-orange-100 text-orange-700" : active.diagnostic.urgency === "Moyenne" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>Urgence : {active.diagnostic.urgency}</span>
                </div>
                {active.diagnostic.summary && <p className="text-sm text-brand-green/70 mt-2">{active.diagnostic.summary}</p>}
                {active.diagnostic.advice && <p className="text-xs text-brand-green/55 mt-1">💡 {active.diagnostic.advice}</p>}
                {active.photo_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${active.photo_url}`} target="_blank" rel="noreferrer" className="inline-block mt-3"><img src={`${process.env.REACT_APP_BACKEND_URL}${active.photo_url}`} alt="incident" className="h-28 rounded-xl border border-brand-soft object-cover" /></a>}
              </div>
            )}

            {/* Quote banner */}
            {active.total_amount > 0 && (
              <div className="mx-6 mt-4 rounded-2xl bg-brand-softer p-4">
                <div className="flex items-center gap-2 text-brand-greenDeep font-semibold mb-2"><FileText className="w-4 h-4" /> Devis</div>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><p className="text-brand-green/50 text-xs">Total</p><p className="font-bold text-brand-greenDeep">{active.total_amount} €</p></div>
                  <div><p className="text-brand-green/50 text-xs">Acompte</p><p className="font-bold text-brand-greenDeep">{active.deposit_amount} €</p></div>
                  <div><p className="text-brand-green/50 text-xs">Solde</p><p className="font-bold text-brand-greenDeep">{(active.total_amount - active.deposit_amount).toFixed(2)} €</p></div>
                </div>
                {active.quote_note && <p className="text-sm text-brand-green/60 mt-2">{active.quote_note}</p>}
                {!isArtisan && (
                  <div className="flex gap-3 mt-4">
                    {!active.deposit_paid && (
                      <button onClick={() => pay("deposit")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors"><CreditCard className="w-4 h-4" /> Payer l'acompte ({active.deposit_amount} €)</button>
                    )}
                    {active.deposit_paid && !active.balance_paid && (
                      <button onClick={() => pay("balance")} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold text-sm transition-colors"><CreditCard className="w-4 h-4" /> Payer le solde ({(active.total_amount - active.deposit_amount).toFixed(2)} €)</button>
                    )}
                    {active.deposit_paid && <span className="inline-flex items-center gap-1.5 text-sm text-brand-limeDark font-medium"><CheckCircle2 className="w-4 h-4" /> Acompte payé</span>}
                    {active.balance_paid && <span className="inline-flex items-center gap-1.5 text-sm text-brand-limeDark font-medium"><CheckCircle2 className="w-4 h-4" /> Travaux réglés</span>}
                  </div>
                )}
              </div>
            )}

            {/* Chat */}
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {active.messages.map((m, i) => {
                const mine = (isArtisan && m.from === "artisan") || (!isArtisan && m.from === "client");
                return (
                  <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl ${mine ? "bg-brand-lime text-brand-greenDeep rounded-br-md" : "bg-brand-softer text-brand-green rounded-bl-md"}`}>
                      <p className="text-sm">{m.text}</p>
                      <p className="text-[10px] opacity-60 mt-0.5">{m.time}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Artisan quote form */}
            {isArtisan && active.status !== "Termin\u00e9" && (
              <div className="px-6 py-3 border-t border-brand-soft bg-brand-softer/40">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-brand-green/70 flex items-center gap-1.5"><Hammer className="w-3.5 h-3.5" /> Proposer / mettre à jour un devis</p>
                  <button onClick={generateAiQuote} disabled={aiLoading} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-greenDeep text-brand-lime font-semibold text-xs hover:bg-brand-green transition-colors disabled:opacity-60">
                    <Sparkles className="w-3.5 h-3.5" /> {aiLoading ? "Génération..." : "Générer avec l'IA"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input type="number" value={quote.total_amount} onChange={(e) => setQuote({ ...quote, total_amount: e.target.value })} placeholder="Total €" className="w-24 px-3 py-2 rounded-lg border border-brand-soft text-sm outline-none" />
                  <input type="number" value={quote.deposit_amount} onChange={(e) => setQuote({ ...quote, deposit_amount: e.target.value })} placeholder="Acompte €" className="w-24 px-3 py-2 rounded-lg border border-brand-soft text-sm outline-none" />
                  <input value={quote.note} onChange={(e) => setQuote({ ...quote, note: e.target.value })} placeholder="Note (optionnel)" className="flex-1 min-w-[120px] px-3 py-2 rounded-lg border border-brand-soft text-sm outline-none" />
                  <button onClick={proposeQuote} className="px-4 py-2 rounded-lg bg-brand-greenDeep text-white font-semibold text-sm hover:bg-brand-green transition-colors">Envoyer le devis</button>
                </div>
                {quote.total_amount > 0 && (
                  <p className="text-[11px] text-brand-green/50 mt-2">Commission plateforme 10% : {(Number(quote.total_amount) * 0.1).toFixed(2)} € · Net perçu : {(Number(quote.total_amount) * 0.9).toFixed(2)} €</p>
                )}
              </div>
            )}

            {/* Message input */}
            <div className="p-4 border-t border-brand-soft flex items-center gap-3">
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Écrivez un message..." className="flex-1 px-4 py-3 rounded-full bg-brand-softer outline-none text-sm focus:ring-2 focus:ring-brand-lime/40 transition-all" />
              <button onClick={sendMsg} className="w-12 h-12 rounded-full bg-brand-lime hover:bg-brand-limeDark flex items-center justify-center text-brand-greenDeep transition-colors"><Send className="w-5 h-5" /></button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-brand-green/50">Sélectionnez une demande</div>
        )}
      </div>
    </div>
  );
}
